import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, HelpCircle, ShieldCheck, Warehouse } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type PartnerKind = "distributor" | "wholesaler";

const content: Record<PartnerKind, {
  badge: string;
  title: string;
  highlight: string;
  summary: string;
  buyFrom: string;
  sellTo: string;
  earn: string;
  steps: { title: string; body: string }[];
  faqs: { q: string; a: string }[];
}> = {
  distributor: {
    badge: "Buy from Bihari Mall. Supply wholesalers.",
    title: "Become a distributor",
    highlight: "for your district",
    summary: "A distributor buys stock from Bihari Mall and supplies wholesalers. You run your own panel for catalogue, stock, orders, and commission.",
    buyFrom: "Admin",
    sellTo: "Wholesalers you appoint",
    earn: "The product’s distributor commission, credited when you approve a wholesaler order",
    steps: [
      { title: "Tell us about your business", body: "Send your business name, contact person, mobile number, and city. There is no public signup form." },
      { title: "Admin opens your account", body: "Bihari Mall reviews the request and creates the distributor login. You receive the email and password from the admin." },
      { title: "Order from the catalogue", body: "Sign in on the normal login page. You land on the distributor panel and order stock from admin." },
      { title: "Supply wholesalers", body: "When a wholesaler order is approved, stock moves to them and your commission is credited." },
    ],
    faqs: [
      { q: "Can I register myself as a distributor?", a: "No. An admin creates the account from the Distributors page and shares the email and password with you." },
      { q: "Who do I buy from?", a: "You buy from Bihari Mall admin. Wholesalers then buy from you." },
      { q: "When is commission credited?", a: "When you approve a wholesaler’s order. The amount is that product’s distributor percent of the customer price, times the quantity. If the percent is 0, stock still moves and the commission is ₹0." },
    ],
  },
  wholesaler: {
    badge: "Buy from a distributor. Supply retailers.",
    title: "Become a wholesaler",
    highlight: "under a distributor",
    summary: "A wholesaler buys stock from one distributor and supplies retailers. Your panel shows that distributor’s catalogue, your stock, orders, and commission.",
    buyFrom: "Your linked distributor",
    sellTo: "Retailers (stores)",
    earn: "The product’s wholesale commission, credited when you approve a retailer order",
    steps: [
      { title: "Choose a distributor", body: "A wholesaler belongs to one distributor. Tell us which distributor you will buy from, plus your business name, contact, mobile, and city." },
      { title: "Admin opens your account", body: "Bihari Mall creates the wholesaler login and links it to that distributor. You receive the email and password from the admin." },
      { title: "Order from your distributor", body: "Sign in on the normal login page. You land on the wholesaler panel and order only what your distributor has in stock." },
      { title: "Supply retailers", body: "When you approve a retailer order, stock moves to that store and your commission is credited." },
    ],
    faqs: [
      { q: "Can I register myself as a wholesaler?", a: "No. An admin creates the account and must link it to an existing distributor before you can place an order." },
      { q: "Who do I buy from?", a: "You buy from the distributor named on your account. You cannot pick a different seller at checkout." },
      { q: "When is commission credited?", a: "When you approve a retailer’s order. The amount is that product’s wholesale percent of the customer price, times the quantity." },
    ],
  },
};

const BecomeChannelPartner = ({ kind }: { kind: PartnerKind }) => {
  const page = content[kind];

  return (
    <div className="min-h-screen bg-background">
      <section className="relative overflow-hidden border-b border-border/40 bg-gradient-to-b from-indigo-50/20 via-background to-background pt-16 pb-20 dark:from-indigo-950/10 lg:pt-24 lg:pb-28">
        <div className="container mx-auto max-w-4xl space-y-6 px-4 text-center">
          <Badge variant="secondary" className="rounded-full border border-primary/20 bg-primary/5 px-3.5 py-1 text-xs font-semibold text-primary">
            {page.badge}
          </Badge>
          <h1 className="text-4xl font-black leading-none tracking-tight sm:text-5xl lg:text-6xl">
            {page.title}{" "}
            <span className="bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 bg-clip-text text-transparent dark:from-violet-400 dark:to-cyan-400">
              {page.highlight}
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {page.summary}
          </p>
          <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
            <Button size="lg" className="h-12 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-8 text-base font-semibold text-white shadow-lg shadow-primary/20 hover:from-violet-500 hover:to-indigo-500" asChild>
              <Link to="/contact">
                Request this account <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="h-12 rounded-xl px-8 text-base font-semibold" asChild>
              <Link to="/auth/login">I already have a login</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto max-w-6xl px-4 py-20">
        <div className="mb-12 space-y-3 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">How this role works</h2>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            Stock and commission move only after the seller above you approves the order.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { title: "You buy from", body: page.buyFrom, icon: Warehouse },
            { title: "You sell to", body: page.sellTo, icon: CheckCircle },
            { title: "You earn", body: page.earn, icon: ShieldCheck },
          ].map((item) => (
            <Card key={item.title} className="border-border/60 shadow-sm">
              <CardHeader>
                <div className="mb-3 w-fit rounded-xl bg-violet-500/10 p-3 text-violet-600 dark:bg-violet-500/20 dark:text-violet-400">
                  <item.icon className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl">{item.title}</CardTitle>
                <CardDescription className="text-sm leading-relaxed">{item.body}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </section>

      <section className="border-y border-border/40 bg-secondary/20 py-20">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="mb-12 space-y-3 text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">How you get a login</h2>
          </div>
          <div className="grid gap-8 md:grid-cols-4">
            {page.steps.map((step, index) => (
              <div key={step.title} className="space-y-3 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border-4 border-background bg-primary text-lg font-bold text-white shadow-md">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold">{step.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto max-w-3xl px-4 py-20">
        <div className="mb-12 space-y-3 text-center">
          <HelpCircle className="mx-auto h-10 w-10 text-primary" />
          <h2 className="text-3xl font-bold tracking-tight">Questions</h2>
        </div>
        <Accordion type="single" collapsible className="w-full rounded-2xl border bg-card p-6 shadow-sm">
          {page.faqs.map((faq, index) => (
            <AccordionItem key={faq.q} value={`item-${index}`} className="py-2">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">{faq.q}</AccordionTrigger>
              <AccordionContent className="leading-relaxed text-muted-foreground">{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="border-t bg-gradient-to-r from-violet-600 to-indigo-700 py-16 text-center text-white">
        <div className="container mx-auto max-w-xl space-y-6 px-4">
          <h2 className="text-3xl font-extrabold">Ready to join the network?</h2>
          <p className="leading-relaxed text-indigo-100">
            Contact Bihari Mall with your business details. Admin creates the login and tells you how to sign in.
          </p>
          <Button size="lg" className="h-12 rounded-xl bg-white px-8 text-base font-semibold text-indigo-700 hover:bg-indigo-50" asChild>
            <Link to="/contact">
              Contact Bihari Mall <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BecomeChannelPartner;
